import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  getAuth,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { motion } from "framer-motion";
import { useNotification } from "@jotrseatrc/shared-components/common/NotificationContext";

export const Actions = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const { showNotification } = useNotification();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (!oobCode || !mode) {
      navigate("/login");
      return;
    }

    const handleAction = async () => {
      try {
        if (mode === "verifyEmail") {
          await applyActionCode(auth, oobCode);
          await auth.currentUser?.reload();
          showNotification("success", "Email berhasil diverifikasi!");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error verifikasi email:", error);

        if (error.code === "auth/invalid-action-code") {
          showNotification(
            "warning",
            "Email sudah diverifikasi sebelumnya atau link tidak valid."
          );
        } else if (error.code === "auth/user-disabled") {
          showNotification("error", "Akun ini telah dinonaktifkan.");
        } else {
          showNotification("error", "Terjadi kesalahan. Silakan coba lagi.");
        }

        navigate("/login");
      }
    };

    if (mode === "verifyEmail") {
      handleAction();
    }
  }, [mode, oobCode, auth, navigate, showNotification]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await verifyPasswordResetCode(auth, oobCode);
      await confirmPasswordReset(auth, oobCode, newPassword);

      setSuccess(true);
      showNotification(
        "success",
        "Password berhasil direset! Mengarahkan ke login..."
      );
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      console.error("Error reset password:", err);

      if (err.code === "auth/invalid-action-code") {
        showNotification(
          "warning",
          "Link reset password sudah digunakan atau tidak valid. Silakan minta reset baru."
        );
      } else {
        showNotification("error", err.message);
      }

      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="w-full min-h-screen flex justify-center items-center relative bg-cover bg-center"
      style={{
        backgroundImage:
          "url(https://images.pexels.com/photos/325185/pexels-photo-325185.jpeg)",
      }}
    >
      <div className="absolute inset-0 bg-cyan-900 bg-opacity-40 backdrop-blur-sm"></div>

      <div className="relative z-1 flex flex-col items-center text-white w-full px-4 md:px-8">
        {mode === "resetPassword" ? (
          <motion.fieldset
            className="w-full sm:w-96 bg-white bg-opacity-40 p-6 rounded-xl shadow-lg backdrop-blur-md"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <h2 className="text-2xl font-semibold text-center mb-4">
              Reset Password
            </h2>

            {success ? (
              <p className="text-white text-center">
                Password berhasil diubah!
              </p>
            ) : (
              <form
                onSubmit={handleResetPassword}
                className="flex flex-col gap-4 text-black"
              >
                <label className="text-left text-white">Password Baru</label>
                <input
                  type="password"
                  placeholder="Masukkan password baru"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600"
                />

                {error && <p className="text-red-500 text-center">{error}</p>}

                <button
                  type="submit"
                  className="mt-4 w-full bg-cyan-600 text-white py-3 rounded-xl hover:bg-cyan-800 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? "Memproses..." : "Reset Password"}
                </button>
              </form>
            )}
          </motion.fieldset>
        ) : (
          <p className="text-center">Memproses...</p>
        )}
      </div>
    </section>
  );
};
